'use client'

import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { useAppConfig } from '@/root/shared-hooks/appConfig/useAppConfig'
import { useGetConfirmationTerms } from '@/root/shared-hooks/sales-static/useGetConfirmationTerms'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PaymentMethod, TicketProps, PAYMENT_METHODS } from '@/root/shared-types/reservation/reservation-types'
import { fetchPaymentMethods } from '@/app/actions/reservation-modification'
import { reservationService } from '@/root/shared-api'
import { PreparePaymentRequestBody } from '@/root/shared-types'
import { useErrorBanner } from '@/root/shared-hooks/useErrorBanner/useErrorBanner'
import { useCustomerForm } from '@/root/modules/shared/ConfirmPage/hooks/useCustomerForm'
import { PersonFormFields, CompanyFormFields } from '@/root/modules/shared/ConfirmPage/const'
import type { RoleOption } from '@/root/modules/shared/ConfirmPage/types'
import { useRoleSelector } from '@/root/modules/shared/ConfirmPage/hooks/useRoleSelector'
import { useDefaultCountry } from '@/root/modules/shared/ConfirmPage/hooks/useDefaultCountry'
import { usePaymentSelection } from '@/root/modules/shared/ConfirmPage/hooks/usePaymentSelection'
import { getTermsByLocale } from '@/root/shared-helpers/reservation/reservation-helpers'

export type UseBookTicketProps = Pick<TicketProps, 'ticketData' | 'sailPackages'> & {
  paymentMethods: PaymentMethod[]
}

/** Everything the confirm (checkout) page renders: owner form, roles, cart totals, terms, payment selection and pay. */
export const useBookTicket = ({
  ticketData: {
    amount,
    there,
    back,
    isRoundTrip,
    charges,
    route,
    reservationId,
    customerInfo,
    token,
    terms: reservationTerms,
  },
  paymentMethods,
  sailPackages,
}: UseBookTicketProps) => {
  const { getTranslation } = useGetTranslations()
  const { formatMoney } = useAppConfig()
  const { locale } = useCustomRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isAgree, setIsAgree] = useState(false)
  const { defaultCountryCode } = useDefaultCountry()

  const customerFormProps = useCustomerForm({ customerInfo })
  const { getOwnerData, reset, triggerValidation, triggerPersonValidation, setCustomerType } = customerFormProps

  // Role selector for authorized users
  const {
    isAuth,
    isProfileLoading,
    roleOptions,
    selectedRole,
    setSelectedRole,
    getRoleFormData,
    creditBalance,
    userId,
    customerId,
    selectedCompanyId,
  } = useRoleSelector()

  const paymentSelection = usePaymentSelection({
    initialPaymentMethods: paymentMethods,
    amount,
    creditBalance,
    isAgree,
    isError,
    loading: isLoading,
  })
  const { hasCreditMethod, replacePaymentMethods } = paymentSelection

  // Apply role data to form fields
  const applyRoleToForm = useCallback(
    (roleCode: string) => {
      const data = getRoleFormData(roleCode)
      if (!data) return

      const companyValues = data.companyData
        ? {
            [CompanyFormFields.LEGAL_NAME]: data.companyData.legalName,
            [CompanyFormFields.REGISTRATION_CODE]: data.companyData.registrationCode,
            [CompanyFormFields.LEGAL_ADDRESS]: data.companyData.legalAddress,
            [CompanyFormFields.ZIP]: data.companyData.zip,
            [CompanyFormFields.CITY]: data.companyData.city,
            [CompanyFormFields.COUNTRY]: data.companyData.country,
          }
        : {
            [CompanyFormFields.LEGAL_NAME]: '',
            [CompanyFormFields.REGISTRATION_CODE]: '',
            [CompanyFormFields.LEGAL_ADDRESS]: '',
            [CompanyFormFields.ZIP]: '',
            [CompanyFormFields.CITY]: '',
            [CompanyFormFields.COUNTRY]: defaultCountryCode,
          }

      reset({
        [PersonFormFields.FIRST_NAME]: data.personData.firstName,
        [PersonFormFields.LAST_NAME]: data.personData.lastName,
        [PersonFormFields.EMAIL]: data.personData.email,
        [PersonFormFields.INTL_CODE]: data.personData.intlCode,
        [PersonFormFields.PHONE_NUMBER]: data.personData.phoneNumber,
        ...companyValues,
      })

      setCustomerType(data.customerType)
    },
    [getRoleFormData, reset, setCustomerType, defaultCountryCode]
  )

  const { confirmationTerms, isLoading: isCmsTermsLoading } = useGetConfirmationTerms(locale)

  const terms =
    confirmationTerms?.find((term) => term.sailpackage === route)?.body ??
    (isCmsTermsLoading ? undefined : getTermsByLocale(reservationTerms, locale)?.ticketTerms)

  const { errorHandler } = useErrorBanner()

  // Shared error handler for API calls
  const handleApiError = useCallback(
    (error: unknown) => {
      if (errorHandler) {
        errorHandler({ error, time: new Date(), message: getTranslation('bookingProcessError') })
        setIsLoading(false)
        setIsError(true)
      }
    },
    [errorHandler, getTranslation]
  )

  // Build owner data directly from role data (bypasses form state which may be stale)
  const buildOwnerFromRole = useCallback(
    (roleCode: string) => {
      const roleData = getRoleFormData(roleCode)
      if (!roleData) return null

      const { personData, companyData, companyId } = roleData
      return {
        firstName: personData.firstName,
        lastName: personData.lastName,
        email: personData.email,
        phone: {
          intlCode: personData.intlCode.replace('+', ''),
          phoneNumber: personData.phoneNumber,
        },
        companyInfo: companyData
          ? {
              legalName: companyData.legalName,
              address: companyData.legalAddress,
              registrationNumber: companyData.registrationCode,
              city: companyData.city,
              zip: companyData.zip,
              country: companyData.country,
              taxNumber: '',
              ruc: '',
              type: 'REGULAR' as const,
            }
          : undefined,
        ...(isAuth && userId != null ? { userId } : {}),
        ...(isAuth && customerId != null ? { customerId } : {}),
        ...(isAuth && companyId != null ? { companyId } : {}),
      }
    },
    [getRoleFormData, isAuth, userId, customerId]
  )

  // Save owner from form values (used on blur when user manually edits fields)
  const saveOwner = useCallback(async () => {
    try {
      const ownerData = {
        ...getOwnerData(),
        ...(isAuth && userId != null ? { userId } : {}),
        ...(isAuth && customerId != null ? { customerId } : {}),
        ...(isAuth && selectedCompanyId != null ? { companyId: selectedCompanyId } : {}),
      }
      await reservationService.postReservationOwner(reservationId, ownerData)
    } catch (error) {
      handleApiError(error)
    }
  }, [getOwnerData, isAuth, userId, customerId, selectedCompanyId, reservationId, handleApiError])

  // Save owner data on blur when person fields are valid
  const onBlur = useCallback(async () => {
    const isValid = await triggerPersonValidation()
    if (isValid) {
      await saveOwner()
    }
  }, [triggerPersonValidation, saveOwner])

  // Save owner directly from role data (bypasses stale form state).
  // The owner decides whether f-edge offers CREDIT, so the methods are refetched when the saved role is a credit
  // company or the current list has CREDIT (leaving the credit company, reload after it)
  const saveOwnerFromRole = useCallback(
    async (roleCode: string) => {
      try {
        const ownerData = buildOwnerFromRole(roleCode)
        if (!ownerData) return
        await reservationService.postReservationOwner(reservationId, ownerData)

        if (!getRoleFormData(roleCode)?.isCreditCompany && !hasCreditMethod) return
        const methodsResult = await fetchPaymentMethods(reservationId)
        if (!methodsResult.success) throw new Error(methodsResult.error.message)
        replacePaymentMethods(methodsResult.data)
      } catch (error) {
        handleApiError(error)
      }
    },
    [buildOwnerFromRole, getRoleFormData, hasCreditMethod, reservationId, replacePaymentMethods, handleApiError]
  )

  // Fill form and save owner once when profile data first loads
  const hasFilledForm = useRef(false)
  useEffect(() => {
    if (isAuth && roleOptions.length > 0 && !hasFilledForm.current) {
      hasFilledForm.current = true
      applyRoleToForm(selectedRole)
      saveOwnerFromRole(selectedRole)
    }
  }, [isAuth, roleOptions.length, applyRoleToForm, selectedRole, saveOwnerFromRole])

  const handleRoleChange = useCallback(
    async (option: RoleOption) => {
      setSelectedRole(option.code)
      applyRoleToForm(option.code)
      await saveOwnerFromRole(option.code)
    },
    [setSelectedRole, applyRoleToForm, saveOwnerFromRole]
  )

  const onPay = async (paymentMethod: string) => {
    // Trigger validation on relevant fields — shows errors on empty/invalid fields
    const isValid = await triggerValidation()
    if (!isValid) {
      // Scroll to the first field with an error
      const firstError = document.querySelector('.border-error')
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    // Save owner before payment (user may not have blurred the last field)
    await saveOwner()

    const requestBody: PreparePaymentRequestBody = {
      reservationId,
      paymentMethod,
      origin: window.origin,
      creditCard: paymentMethod === PAYMENT_METHODS.CARD,
      locale: locale,
    }
    setIsLoading(true)

    try {
      const result = await reservationService.preparePayment(requestBody)
      if (!result) {
        throw new Error('Payment failed: no response from server')
      }
      if (paymentMethod === PAYMENT_METHODS.CREDIT) {
        // Credit payment is processed immediately by F-Edge.
        // F-Edge returns redirectUrl pointing to production, so we navigate locally instead.
        window.location.href = `/${locale}/ticket/${token}`
      } else {
        window.location.href = result.redirectUrl
      }
    } catch (error) {
      handleApiError(error)
    }
  }

  const onAgree = useCallback(() => setIsAgree((prev) => !prev), [])

  const commonColumns = [
    getTranslation('confirmationQty'),
    getTranslation('confirmationPrice'),
    getTranslation('confirmationSubtotal'),
  ]

  const itemColumns = [getTranslation('confirmationTicketType'), ...commonColumns]
  const chargesColumns = [getTranslation('confirmationDescription'), ...commonColumns]

  const backHref = useMemo(() => {
    const sorted = (sailPackages ?? []).toSorted((packageA, packageB) => {
      const departureDateA = packageA.sailRefs?.[0]?.departureDate ?? ''
      const departureDateB = packageB.sailRefs?.[0]?.departureDate ?? ''
      return departureDateA.localeCompare(departureDateB)
    })
    const codes = sorted.map((sailPackage) => sailPackage.code).join(',')
    const sailRefIds = sorted
      .flatMap((sailPackage) => sailPackage.sailRefs?.map((sailRef) => sailRef.sailRefId) ?? [])
      .join(',')
    return `/${locale}/select-tickets/${codes}/${sailRefIds}?token=${token}`
  }, [locale, sailPackages, token])

  const onProceed = () => {
    if (paymentSelection.selectedMethod) onPay(paymentSelection.selectedMethod)
  }

  return {
    ...paymentSelection,
    backHref,
    onProceed,
    there,
    back,
    itemColumns,
    isRoundTrip,
    amount,
    amountFormatted: formatMoney(amount),
    chargesTable: {
      ...charges,
      description: getTranslation('confirmationOnlineChargesLabel'),
      columns: chargesColumns,
      isRoundTrip: isRoundTrip,
    },
    confirmationTotalText: getTranslation('confirmationTotalFull'),
    terms,
    isAgree,
    onAgree,
    onPay,
    warningText: getTranslation('confirmationPaymentWarning'),
    loading: isLoading,
    customerInfo,
    customerFormProps: {
      ...customerFormProps,
      onBlur,
      isAuth,
      roleOptions,
      selectedRole,
      selectedRoleType: customerFormProps.customerType,
      onRoleChange: handleRoleChange,
      isProfileLoading,
    },
    isError,
  }
}
